import { useEffect, useState, useRef } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useNavigate, useParams, useOutletContext } from 'react-router-dom';
import { yupResolver } from '@hookform/resolvers/yup';
import { makeStyles } from 'tss-react/mui';
import { AxiosError } from 'axios';
import moment from 'moment';

import CancelIcon from '@mui/icons-material/Cancel';
import AddIcon from '@mui/icons-material/Add';
import SaveIcon from '@mui/icons-material/Save';
import { Box, Theme, Typography } from '@mui/material';

import { handleGetDefendants, mapDefendantsQueryResults } from '../helpers';
import { DeductionEditFormSubmitProps } from '../types';
import { DeductionTypeMap } from '../SettlementsView/constants';

import {
  deleteDeductionRecord,
  deleteDocument,
  getDocument,
  getRemittanceContextByDeduction,
  updateDeductionRecord,
} from '../../../api/paymentsApi/deductionsApi';
import {
  ActiveVendorAccountOption,
  getActiveVendorAccounts,
  getVendorPaymentMethodDetails,
} from '../../../api/vendorApi/vendorAccountsApi';
import CommonButton from '../../../components/buttons/CommonButton';
import FormLinkButton from '../../../components/buttons/FormLinkButton';
import FormCopyTextField from '../../../components/formFields/FormCopyTextField';
import FormTextField from '../../../components/formFields/FormTextField';
import FormToggleField from '../../../components/formFields/FormToggleField';
import FormSelectField from '../../../components/formFields/FormSelectField';
import FormSubmitButton from '../../../components/buttons/FormSubmitButton';
import FormCancelButton from '../../../components/buttons/FormCancelButton';
import FormPDFUploadField from '../../../components/formFields/FormPDFUploadField';
import { DEDUCTION_TYPES, INTENT_TYPE_OPTIONS, PAYMENT_METHOD_OPTIONS } from '../../../constants/constants';
import { deductionsEditFormSchema } from '../../../helpers/formSchemas/formSchemas';
import { DEDUCTIONS_EDIT_FORM_DEFAULT_VALUES } from '../../../helpers/formSchemas/formDefaultValues';
import usePageTitle from '../../../hooks/usePageTitle';
import useSnackBar from '../../../hooks/useSnackBar';
import { useHasPermission } from '../../../hooks/useHasPermission';
import { EntityFlexibleRecord, PaginationQueryParams } from '../../../types';
import Popup from '../../../components/Popup';
import { POPUP_DESCRIPTIONS } from '../../../constants/popupDescriptions';
import { mapVendorsQueryResults } from '../../CasesView/helpers';
import FormDateField from '../../../components/formFields/FormDateField';
import { getVendorDefaultPaymentAccountId, getVendorPaymentAccountOptions } from '../../../helpers/vendorPaymentAccounts';
import FormAutocompleteFieldServerSearch from '../../../components/formFields/FormAutocompleteFieldServerSearch';
import RemittanceAccordion from './RemittanceAccordion';
import LienRecoveryPopup from '../../../components/LienRecoveryPopup';
import { renderPaymentAccountInfo, renderPaymentAccountOption, renderPaymentAccountSelectedValue } from '../paymentAccountDisplayHelpers';

const DEDUCTION_PAYMENT_METHOD_OPTIONS = PAYMENT_METHOD_OPTIONS.filter(
  (option) => String(option.id).toUpperCase() !== 'V',
);

const useStyles = makeStyles()((theme: Theme) => ({
  pageContainer: {
    display: 'flex',
    width: '100%',
    maxWidth: '100%',
    minWidth: 0,
  },
  formContainer: {
    display: 'flex',
    flexBasis: '1184px',
    width: '100%',
    maxWidth: '100%',
    minWidth: 0,

    '@media (max-width: 900px)': {
      flexBasis: '100%',
    },
  },
  formWrapper: {
    display: 'flex',
    gap: '64px',
    width: '100%',
    maxWidth: '100%',
    minWidth: 0,

    '@media (max-width: 900px)': {
      flexDirection: 'column',
      gap: 0,
    },
  },
  formInnerWrapper: {
    display: 'flex',
    flexDirection: 'column',
    padding: '32px',
    backgroundColor: theme.palette.primary.light,
    borderRadius: '16px',
    flexBasis: '896px',
    width: '100%',
    maxWidth: '100%',
    minWidth: 0,

    '@media (max-width: 900px)': {
      flexBasis: '416px',
      padding: '12px',
    },
  },
  formInfoContainer: {
    display: 'flex',
    width: '100%',
    maxWidth: '100%',
    minWidth: 0,
    gap: '24px',

    '@media (max-width: 900px)': {
      flexDirection: 'column',
    },
  },
  fieldHeadline: {
    fontWeight: 600,
    fontSize: '20px',
    lineHeight: '24px',
    color: theme.palette.common.white,
    fontFamily: 'Inter',
  },
  formFirstInfoBlock: {
    display: 'flex',
    flexDirection: 'column',
    flexBasis: '416px',

    '@media (max-width: 900px)': {
      flexBasis: 0,
    },
  },
  formSecondInfoBlock: {
    display: 'flex',
    flexDirection: 'column',
    flexBasis: '416px',

    '@media (max-width: 900px)': {
      flexBasis: 0,
      marginTop: '32px',
    },
  },
  typeSectionHeadline: {
    marginTop: '40px',
    fontWeight: 600,
    fontSize: '20px',
    lineHeight: '24px',
    color: theme.palette.common.white,
    fontFamily: 'Inter',

    '@media (max-width: 850px)': {
      marginTop: '32px',
    },
  },
  formActionsWrapper: {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    flexBasis: '200px',

    '@media (max-width: 900px)': {
      flexBasis: 0,
    },
  },
}));

const DeductionsEditFormContainer = () => {
  const styles = useStyles();
  const { deductionId } = useParams();
  const { setPageTitle } = usePageTitle();
  const navigate = useNavigate();
  const { setAlert } = useSnackBar();
  const { entityData } = useOutletContext<EntityFlexibleRecord>();

  const canEdit = useHasPermission('Payments', 'Edit');
  const canDelete = useHasPermission('Payments', 'Delete');
  const canViewCases = useHasPermission('Cases', 'View');
  const canViewDefendants = useHasPermission('Defendants', 'View');
  const canViewVendors = useHasPermission('Vendors', 'View');

  const [defaultPaymentMethod, setDefaultPaymentMethod] = useState<string>('');
  const [isFormSubmitting, setIsFormSubmitting] = useState<boolean>(false);
  const [isDeletePopupOpen, setIsDeletePopupOpen] = useState<boolean>(false);
  const [isDeleteInvoicePopupOpen, setIsDeleteInvoicePopupOpen] = useState<boolean>(false);
  const [isPaymentMethodMissing, setIsPaymentMethodMissing] = useState<boolean>(false);
  const [isPaymentProvisioningIssue, setIsPaymentProvisioningIssue] = useState<boolean>(false);
  const [isVendorsLoaded, setIsVendorsLoaded] = useState<boolean>(false);
  const [isRemittanceFormOpen, setIsRemittanceFormOpen] = useState(false);
  const [isLienRecoveryPopupOpen, setIsLienRecoveryPopupOpen] = useState(false);

  // Recoveries apply only to Lien and Service Provider deductions
  const canCreateRecovery =
    canEdit && (entityData?.type === 'Lien' || entityData?.type === 'SERVICE_PROVIDER');

  const isInitialLoadRef = useRef<boolean>(true);
  const previousDispositionRef = useRef<string | undefined>();

  // Tracks only changes to the deduction/settled amount.
  // Remittance row changes themselves should never trigger amount synchronization.
  const previousDeductionAmountRef = useRef<string | null>(null);

  const [activeVendors, setActiveVendors] = useState<ActiveVendorAccountOption[]>([]);

  const [vendorPaymentAccountsById, setVendorPaymentAccountsById] = useState<
    Record<
      string,
      {
        achAccounts?: ActiveVendorAccountOption['achAccounts'];
        checkAccounts?: ActiveVendorAccountOption['checkAccounts'];
      }
    >
  >({});

  const [paymentAccountOptions, setPaymentAccountOptions] = useState<Array<{
    id: string;
    label: string;
    subtitle?: string;
    isLastUsedAccount?: boolean;
    isUsedAccount?: boolean;
    isNotUsedAccount?: boolean;
    isFailedAccount?: boolean;
  }>>([]);

  const [remittanceTemplate, setRemittanceTemplate] = useState<any | null>(null); // eslint-disable-line @typescript-eslint/no-explicit-any
  const [isContextLoading, setIsContextLoading] = useState(false);
  const [hasRequestedRemittanceContext, setHasRequestedRemittanceContext] = useState(false);
  const [remittanceContext, setRemittanceContext] = useState<any | null>(null); // eslint-disable-line @typescript-eslint/no-explicit-any

  const formMethods = useForm({
    mode: 'onChange',
    reValidateMode: 'onChange',
    resolver: yupResolver(deductionsEditFormSchema),
    defaultValues: DEDUCTIONS_EDIT_FORM_DEFAULT_VALUES,
  });

  const {
    handleSubmit,
    reset,
    watch,
    setValue,
    getValues,
    formState: { isValid, isDirty, dirtyFields },
  } = formMethods;

  const caseIdValue = watch('caseReferenceId');
  const defendantIdValue = watch('defendant.id');
  const isAssignedToggleValue = watch('isAssigned');
  const typeFieldValue = watch('type');
  const vendorAccountIdValue = watch('vendorAccount.id');
  const vendorAccountValue = watch('vendorAccount');
  const paymentMethodValue = watch('paymentMethod');
  const paymentAccountValue = watch('paymentAccount');
  const preSettlementAmountValue = watch('preSettlementAmount');
  const dispositionValue = watch('disposition');
  const remittanceLinesValue = watch('remittanceLines') || [];
  const amountValue = watch('amount') ?? '';
  const canUpdate = entityData?.canUpdate as boolean;

  const selectedPaymentAccount = paymentAccountOptions.find(
    (item) => item.id === String(paymentAccountValue?.id),
  );

  const [isFailedAccountConfirmOpen, setIsFailedAccountConfirmOpen] = useState<boolean>(false);
  const pendingSubmitValuesRef = useRef<DeductionEditFormSubmitProps | null>(null);

  const remittanceTotal = Array.isArray(remittanceLinesValue)
    ? remittanceLinesValue.reduce((total, line) => {
      if (typeof line.amount === 'number' && Number.isFinite(line.amount)) {
        return total + line.amount;
      }

      return total;
    }, 0)
    : 0;

  const deductionAmountNumber = Number(amountValue || 0);

  const isCheckRemittanceBalanced =
    (paymentMethodValue || '').toUpperCase() === 'CHECK' && !!remittanceContext && !!remittanceTemplate
      ? Math.round(deductionAmountNumber * 100) === Math.round(remittanceTotal * 100)
      : true;

  const normalizedPaymentMethodValue = (paymentMethodValue || '').toUpperCase();

  const isPaymentAccountRequired = normalizedPaymentMethodValue === 'ACH' || normalizedPaymentMethodValue === 'CHECK';
  const isVendorType = typeFieldValue === 'Lien' || typeFieldValue === 'SERVICE_PROVIDER';
  const isVendorAccountMissing = isVendorType && !vendorAccountIdValue;

  const normalizePaymentMethod = (paymentMethod?: string | null) => {
    return paymentMethod && paymentMethod !== '-' ? paymentMethod : '';
  };

  const buildDefaultRemittanceValues = (
    template: {
      columns?: Array<{ binding: string; key: string }>;
    } | null,
    defaults: Record<string, string>,
  ): Record<string, string> => {
    const columns = template && Array.isArray(template?.columns) ? template.columns : [];

    const values: Record<string, string> = {};

    for (const column of columns) {
      if (column.binding === 'LINE_AMOUNT') {
        continue;
      }

      values[column.key] = defaults[column.key] ?? '';
    }

    return values;
  };

  const getDefaultRemittanceLine = (
    template: {
      columns?: Array<{ binding: string; key: string }>;
    } | null,
    defaults: Record<string, string>,
  ) => {
    const rawAmount = String(amountValue ?? '').trim();

    const parsedAmount = rawAmount ? Number(rawAmount.replace(/[$,\s]/g, '')) : null;

    return {
      id: 1,
      amount: Number.isFinite(parsedAmount as number) ? parsedAmount : null,
      values: buildDefaultRemittanceValues(template, defaults),
    };
  };

  const handleGetActiveVendors = async (params: PaginationQueryParams, type?: string) => {
    const currentType = type || typeFieldValue;

    let vendorTypeFilter: string | undefined;

    if (currentType === 'Lien') {
      vendorTypeFilter = 'LIEN_HOLDER';
    } else if (currentType === 'SERVICE_PROVIDER') {
      vendorTypeFilter = 'SERVICE_PROVIDER';
    }

    const filters = vendorTypeFilter ? { vendorType: vendorTypeFilter } : undefined;

    const response = await getActiveVendorAccounts(params.match, filters);

    setActiveVendors(response?.data || []);
    setIsVendorsLoaded(true);

    return {
      data: {
        resultSet: response.data,
        totalCount: response.data.length,
      },
    };
  };

  const onDeleteButtonClick = () => setIsDeletePopupOpen(true);

  const onDeleteInvoiceButtonClick = () => setIsDeleteInvoicePopupOpen(true);

  const handleCloseDeletePopup = () => setIsDeletePopupOpen(false);

  const handleCloseDeleteInvoicePopup = () => setIsDeleteInvoicePopupOpen(false);

  const handleCancelChanges = () => reset();

  const handleTypeChange = (value: string) => {
    setValue('type', value, {
      shouldDirty: true,
      shouldValidate: true,
    });

    setValue('vendorAccount', null, {
      shouldDirty: true,
      shouldValidate: true,
    });

    setValue('paymentMethod', '', {
      shouldDirty: true,
      shouldValidate: true,
    });

    setValue('paymentAccount', null, {
      shouldDirty: true,
      shouldValidate: true,
    });

    setDefaultPaymentMethod('');
  };

  const submitDeductionForm = (values: DeductionEditFormSubmitProps) => {
    setIsFormSubmitting(true);

    const typeValue = values.type === 'Expense Reimbursement' ? 'Advance' : values.type;

    const formData = new FormData();

    formData.append('defendantId', !values.defendant.id?.length ? '' : String(values.defendant.id));

    formData.append('amount', values.amount);
    formData.append('preSettlementAmount', values.preSettlementAmount || '');
    formData.append('type', typeValue);
    formData.append('disposition', values.disposition || '');
    // formData.append('description', values.description || '');
    formData.append('memo', values.memo || '');

    if (typeValue === 'Lien' || typeValue === 'SERVICE_PROVIDER') {
      formData.append('vendorFirmParticipationId', values.vendorAccount?.id ? String(values.vendorAccount.id) : '');

      formData.append('paymentMethod', values.paymentMethod || '');

      if (values.paymentAccount?.id) {
        formData.append('vendorPaymentAccountId', String(values.paymentAccount.id));
      }
    }

    if (dispositionValue === 'External') {
      if (typeValue === 'Advance') {
        formData.append('paymentMethod', values.paymentMethod || '');
      }

      formData.append('externalPaymentReference', values.externalPaymentReference || '');

      const formattedDate =
        values.externalPaymentDate instanceof Date ? values.externalPaymentDate.toISOString() : values.externalPaymentDate || '';

      formData.append('externalPaymentDate', formattedDate);
    }

    const isVendorCheckRemittance =
      (typeValue === 'Lien' || typeValue === 'SERVICE_PROVIDER') && (values.paymentMethod || '').toUpperCase() === 'CHECK';

    const hasRemittanceLineChanges = !!dirtyFields.remittanceLines;

    const existingRemittanceRows = Array.isArray((entityData as any)?.remittanceLines) ? (entityData as any).remittanceLines : []; // eslint-disable-line @typescript-eslint/no-explicit-any

    const hasNewDefaultRows =
      existingRemittanceRows.length === 0 && Array.isArray(remittanceLinesValue) && remittanceLinesValue.length > 0;

    const shouldSubmitRemittanceLines = hasRemittanceLineChanges || hasNewDefaultRows;

    const shouldClearExistingRemittance = !isVendorCheckRemittance && existingRemittanceRows.length > 0;

    if (isVendorCheckRemittance && shouldSubmitRemittanceLines) {
      const payloadRemittanceLines = (remittanceLinesValue || []).map(({ amount, values }) => ({
        amount,
        values,
      }));

      formData.append('remittanceLines', JSON.stringify(payloadRemittanceLines));
    }

    if (shouldClearExistingRemittance) {
      formData.append('remittanceLines', JSON.stringify([]));
    }

    if (values.uploadInvoice) {
      formData.append('document', values.uploadInvoice);
    }

    updateDeductionRecord(deductionId as string, formData)
      .then((res) => {
        if (!res.data.error) {
          setAlert((prev) => ({
            ...prev,
            message: 'Deduction updated successfully.',
            type: 'success',
          }));

          navigate('/payments/deductions');
        }
      })
      .catch((error) => {
        const axiosError = error as AxiosError<{ message?: string }>;

        setAlert((prev) => ({
          ...prev,
          message: axiosError.response?.data?.message || 'Failed to update deduction',
          type: 'error',
        }));
      })
      .finally(() => {
        setIsFormSubmitting(false);
      });
  };

  const onSubmitForm = (values: DeductionEditFormSubmitProps) => {
    if (selectedPaymentAccount?.isFailedAccount) {
      pendingSubmitValuesRef.current = values;
      setIsFailedAccountConfirmOpen(true);
      return;
    }

    submitDeductionForm(values);
  };

  const handleConfirmFailedAccountSubmit = () => {
    setIsFailedAccountConfirmOpen(false);

    const values = pendingSubmitValuesRef.current;
    pendingSubmitValuesRef.current = null;

    if (values) {
      submitDeductionForm(values);
    }
  };

  const handleCancelFailedAccountSubmit = () => {
    setIsFailedAccountConfirmOpen(false);
    pendingSubmitValuesRef.current = null;
  };

  const handleDeleteDeduction = () => {
    deleteDeductionRecord(deductionId as string)
      .then(() => {
        setAlert((prev) => ({
          ...prev,
          message: 'Deduction deleted successfully.',
          type: 'success',
        }));

        navigate('/payments/deductions');
      })
      .catch((error) => {
        const axiosError = error as AxiosError<{ message?: string }>;

        setAlert((prev) => ({
          ...prev,
          message: axiosError.response?.data?.message || 'Failed to delete deduction',
          type: 'error',
        }));

        handleCloseDeletePopup();
      });
  };

  const handleDownloadFile = async () => {
    try {
      const response = await getDocument(deductionId as string);

      if (response.data?.downloadUrl) {
        const link = document.createElement('a');

        link.href = response.data.downloadUrl;
        link.download = '';
        link.target = '_blank';

        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } else {
        setAlert((prev) => ({
          ...prev,
          message: 'No file found to download.',
          type: 'info',
        }));
      }
    } catch (error) {
      const axiosError = error as AxiosError<{ message?: string }>;

      setAlert((prev) => ({
        ...prev,
        message: axiosError.response?.data?.message || 'File download error',
        type: 'error',
      }));
    }
  };

  const handleDeleteFile = async () => {
    deleteDocument(deductionId as string)
      .then(() => {
        if (entityData) {
          entityData.docFileName = null;
        }

        setAlert((prev) => ({
          ...prev,
          message: 'Invoice deleted successfully.',
          type: 'success',
        }));
      })
      .catch((error) => {
        const axiosError = error as AxiosError<{ message?: string }>;

        setAlert((prev) => ({
          ...prev,
          message: axiosError.response?.data?.message || 'Failed to delete invoice',
          type: 'error',
        }));
      })
      .finally(() => {
        handleCloseDeleteInvoicePopup();
      });
  };

  /**
   * Initial entity load.
   *
   * Important:
   * previousDeductionAmountRef is initialized to the backend
   * amount after reset so loading the deduction does NOT count
   * as a user changing the settled amount.
   */
  useEffect(() => {
    if (!entityData) return;

    const typeDisplay = DeductionTypeMap[entityData.type as string] || (entityData.type as string);

    const normalizedPaymentMethod = normalizePaymentMethod(entityData.paymentMethod as string);

    const initialPaymentAccountId = entityData.vendorPaymentAccountId ?? entityData.paymentAccountId;

    const initialPaymentAccountName = entityData.vendorPaymentAccountName ?? entityData.paymentAccountName;

    setPageTitle && setPageTitle(typeDisplay);

    reset({
      ...entityData,
      paymentMethod: normalizedPaymentMethod,
      paymentAccount: initialPaymentAccountId
        ? {
          id: String(initialPaymentAccountId),
          label: String(initialPaymentAccountName || ''),
        }
        : null,
      remittanceLines: Array.isArray(entityData.remittanceLines) ? entityData.remittanceLines : [],
      uploadInvoice: null,
      vendorAccount:
        entityData.type === 'Lien' || entityData.type === 'SERVICE_PROVIDER'
          ? (entityData.vendorAccount as {
            id?: string;
            label?: string;
            defaultPaymentMethod?: string;
          } | null)
          : null,
    });

    // Critical:
    // Existing backend amount is the synchronization baseline.
    previousDeductionAmountRef.current = String(entityData.amount ?? '');

    previousDispositionRef.current = entityData.disposition as string | undefined;

    setTimeout(() => {
      isInitialLoadRef.current = false;
      formMethods.trigger();
    }, 0);

    return () => {
      setPageTitle && setPageTitle('');
    };
  }, [entityData]);

  useEffect(() => {
    if (!isVendorsLoaded) {
      setIsPaymentMethodMissing(false);
      return;
    }

    if (!isPaymentAccountRequired) {
      setIsPaymentMethodMissing(false);
      return;
    }

    const selectedVendor = activeVendors.find((vendor) => String(vendor.id) === String(vendorAccountIdValue));

    const selectedActivePaymentMethod = selectedVendor?.activePaymentMethod || [];

    setIsPaymentMethodMissing(!!paymentMethodValue && !selectedActivePaymentMethod.includes(paymentMethodValue));
  }, [paymentMethodValue, vendorAccountIdValue, activeVendors, isVendorsLoaded, isPaymentAccountRequired]);

  useEffect(() => {
    if (!isVendorsLoaded || !isPaymentAccountRequired) {
      setIsPaymentProvisioningIssue(false);
      return;
    }

    const selectedVendor = activeVendors.find((vendor) => String(vendor.id) === String(vendorAccountIdValue));

    const selectedActivePaymentMethod = selectedVendor?.activePaymentMethod || [];

    setIsPaymentMethodMissing(!!paymentMethodValue && !selectedActivePaymentMethod.includes(paymentMethodValue));
  }, [
    paymentMethodValue,
    paymentAccountValue?.id,
    vendorAccountIdValue,
    activeVendors,
    isVendorsLoaded,
    isPaymentAccountRequired,
  ]);

  useEffect(() => {
    if (!isVendorsLoaded || !isPaymentAccountRequired) {
      setIsPaymentProvisioningIssue(false);
      return;
    }

    const selectedVendor = activeVendors.find((vendor) => String(vendor.id) === String(vendorAccountIdValue));
    const normalizedPaymentMethod = (paymentMethodValue || '').trim().toUpperCase();
    const selectedPaymentAccountId = paymentAccountValue?.id ? String(paymentAccountValue.id) : '';

    if (!selectedVendor || !selectedPaymentAccountId) {
      setIsPaymentProvisioningIssue(false);
      return;
    }

    const provisioningList =
      normalizedPaymentMethod === 'ACH'
        ? selectedVendor.activeBankAccounts || []
        : normalizedPaymentMethod === 'CHECK'
          ? selectedVendor.activeMailingAddresses || []
          : [];

    if (!provisioningList.length) {
      setIsPaymentProvisioningIssue(false);
      return;
    }

    const selectedProvisioningAccount = provisioningList.find((account) => String(account.id) === selectedPaymentAccountId);

    setIsPaymentProvisioningIssue(!!selectedProvisioningAccount && selectedProvisioningAccount.provisionStatus !== 'Completed');
  }, [
    paymentMethodValue,
    paymentAccountValue?.id,
    vendorAccountIdValue,
    activeVendors,
    isVendorsLoaded,
    isPaymentAccountRequired,
  ]);

  useEffect(() => {
    if (!vendorAccountValue?.defaultPaymentMethod) {
      return;
    }

    setDefaultPaymentMethod(vendorAccountValue.defaultPaymentMethod);

    if (dispositionValue !== 'External' && !paymentMethodValue) {
      setValue('paymentMethod', vendorAccountValue.defaultPaymentMethod, {
        shouldDirty: false,
        shouldValidate: true,
      });
    }
  }, [vendorAccountValue, dispositionValue, paymentMethodValue, setValue]);

  useEffect(() => {
    if (!vendorAccountIdValue || !paymentMethodValue || !isPaymentAccountRequired) {
      setPaymentAccountOptions([]);

      setValue('paymentAccount', null, {
        shouldDirty: false,
        shouldValidate: true,
      });

      return;
    }

    const vendorIdKey = String(vendorAccountIdValue);

    const vendorId = Number(vendorAccountIdValue);

    if (!vendorPaymentAccountsById[vendorIdKey] && Number.isFinite(vendorId)) {
      getVendorPaymentMethodDetails(vendorId)
        .then((response) => {
          setVendorPaymentAccountsById((prev) => ({
            ...prev,
            [vendorIdKey]: {
              achAccounts: response.data.achAccounts,
              checkAccounts: response.data.checkAccounts,
            },
          }));
        })
        .catch(() => {
          setVendorPaymentAccountsById((prev) => ({
            ...prev,
            [vendorIdKey]: {
              achAccounts: [],
              checkAccounts: [],
            },
          }));
        });
    }

    const selectedVendor = activeVendors.find((vendor) => String(vendor.id) === String(vendorAccountIdValue));

    const selectedVendorAccounts = vendorPaymentAccountsById[vendorIdKey];

    const vendorForPaymentAccounts = selectedVendor
      ? {
        ...selectedVendor,
        achAccounts: selectedVendorAccounts?.achAccounts,
        checkAccounts: selectedVendorAccounts?.checkAccounts,
      }
      : selectedVendorAccounts;

    const normalizedPaymentMethod = (paymentMethodValue || '').trim().toUpperCase();

    const accountDetails: Array<{
      id: number | string;
      isLastUsedAccount?: boolean;
      isUsedAccount?: boolean;
      isNotUsedAccount?: boolean;
      isFailedAccount?: boolean;
    }> =
      normalizedPaymentMethod === 'ACH'
        ? selectedVendorAccounts?.achAccounts || []
        : normalizedPaymentMethod === 'CHECK'
          ? selectedVendorAccounts?.checkAccounts || []
          : [];

    const options = getVendorPaymentAccountOptions(vendorForPaymentAccounts, paymentMethodValue).map((item) => {
      const account = accountDetails.find(
        (account) => String(account.id) === String(item.id),
      );

      return {
        id: String(item.id),
        label: item.subtitle ? `${item.label} - ${item.subtitle}` : item.label,
        subtitle: item.subtitle,
        isLastUsedAccount: account?.isLastUsedAccount,
        isUsedAccount: account?.isUsedAccount,
        isNotUsedAccount: account?.isNotUsedAccount,
        isFailedAccount: account?.isFailedAccount,
      };
    });

    setPaymentAccountOptions(options);

    const hasCurrentSelection = !!paymentAccountValue?.id && options.some((item) => item.id === String(paymentAccountValue.id));

    if (hasCurrentSelection) {
      return;
    }

    const initialPaymentAccountId = entityData?.vendorPaymentAccountId
      ? String(entityData.vendorPaymentAccountId)
      : entityData?.paymentAccountId
        ? String(entityData.paymentAccountId)
        : '';

    const initialOption = options.find((item) => item.id === initialPaymentAccountId);

    if (initialOption) {
      setValue(
        'paymentAccount',
        {
          id: initialOption.id,
          label: initialOption.label,
        },
        {
          shouldDirty: false,
          shouldValidate: true,
        },
      );

      return;
    }

    const defaultAccountId = getVendorDefaultPaymentAccountId(vendorForPaymentAccounts, paymentMethodValue);

    if (defaultAccountId) {
      const defaultOption = options.find((item) => item.id === defaultAccountId);

      if (defaultOption) {
        setValue(
          'paymentAccount',
          {
            id: defaultOption.id,
            label: defaultOption.label,
          },
          {
            shouldDirty: false,
            shouldValidate: true,
          },
        );

        return;
      }
    }

    setValue('paymentAccount', null, {
      shouldDirty: false,
      shouldValidate: true,
    });
  }, [
    vendorAccountIdValue,
    paymentMethodValue,
    isPaymentAccountRequired,
    activeVendors,
    vendorPaymentAccountsById,
    paymentAccountValue?.id,
    entityData,
    setValue,
  ]);

  useEffect(() => {
    if (!vendorAccountIdValue || !isVendorsLoaded || !activeVendors.length) {
      return;
    }

    const foundVendor = activeVendors.find((vendor) => String(vendor.id) === String(vendorAccountIdValue));

    if (!foundVendor?.defaultVendorPaymentMethod) {
      return;
    }

    setDefaultPaymentMethod(foundVendor.defaultVendorPaymentMethod);

    if (vendorAccountValue && !vendorAccountValue.defaultPaymentMethod) {
      setValue(
        'vendorAccount',
        {
          id: vendorAccountValue?.id || '',
          label: vendorAccountValue?.label || '',
          defaultPaymentMethod: foundVendor.defaultVendorPaymentMethod,
          accounting: foundVendor.accounting || undefined,
        },
        {
          shouldDirty: false,
          shouldValidate: false,
        },
      );
    }

    if (dispositionValue !== 'External' && !paymentMethodValue) {
      setValue('paymentMethod', foundVendor.defaultVendorPaymentMethod, {
        shouldDirty: false,
        shouldValidate: true,
      });
    }
  }, [vendorAccountIdValue, activeVendors, isVendorsLoaded, vendorAccountValue, dispositionValue, paymentMethodValue, setValue]);

  useEffect(() => {
    if (isInitialLoadRef.current) {
      return;
    }

    const previousDisposition = previousDispositionRef.current;

    if (previousDisposition === dispositionValue) {
      return;
    }

    if (dispositionValue === 'External') {
      setValue('paymentMethod', '', {
        shouldDirty: true,
        shouldValidate: true,
      });

      setValue('paymentAccount', null, {
        shouldDirty: true,
        shouldValidate: true,
      });

      setValue('externalPaymentReference', '', {
        shouldDirty: true,
      });

      setValue('externalPaymentDate', null, {
        shouldDirty: true,
      });
    }

    if ((dispositionValue === 'Pay' || dispositionValue === 'Withhold') && defaultPaymentMethod) {
      setValue('paymentMethod', defaultPaymentMethod, {
        shouldDirty: true,
        shouldValidate: true,
      });
    }

    previousDispositionRef.current = dispositionValue;
  }, [dispositionValue, defaultPaymentMethod, setValue]);

  useEffect(() => {
    if (typeFieldValue !== 'Lien' && typeFieldValue !== 'SERVICE_PROVIDER' && vendorAccountValue !== null) {
      setValue('vendorAccount', null, {
        shouldDirty: true,
      });

      setValue('paymentAccount', null, {
        shouldDirty: true,
        shouldValidate: true,
      });
    }

    formMethods.trigger(['vendorAccount']);
  }, [typeFieldValue, vendorAccountValue, setValue]);

  /**
   * Loads remittance context.
   *
   * Existing remittance rows are preserved in Edit.
   * If no existing rows are present, context rows/default row
   * can be used.
   */
  useEffect(() => {
    const isCheckPayment = (paymentMethodValue || '').toUpperCase() === 'CHECK';

    if (!isCheckPayment) {
      setHasRequestedRemittanceContext(false);
      setIsContextLoading(false);
      setRemittanceContext(null);
      setRemittanceTemplate(null);

      setValue('remittanceContext', null, {
        shouldDirty: false,
        shouldValidate: true,
      });

      setValue('remittanceLines', [], {
        shouldDirty: false,
        shouldValidate: true,
      });

      return;
    }

    setHasRequestedRemittanceContext(true);
    setIsContextLoading(true);

    getRemittanceContextByDeduction(deductionId)
      .then((response) => {
        const context = response?.data ?? null;

        const hasContext = !!context && !!context.remittanceTemplate;

        const hasInitialLines = Array.isArray(context?.remittanceLines) && context.remittanceLines.length > 0;

        const defaultLine = hasContext
          ? getDefaultRemittanceLine(context.remittanceTemplate, context.prefetchedValues ?? {})
          : null;

        const entityRemittanceLines = Array.isArray(entityData?.remittanceLines) ? entityData.remittanceLines : [];

        const nextRemittanceLines =
          entityRemittanceLines.length > 0
            ? entityRemittanceLines
            : hasInitialLines
              ? context.remittanceLines
              : hasContext
                ? [defaultLine]
                : [];

        setRemittanceContext(context ?? null);

        setRemittanceTemplate(context?.remittanceTemplate ?? null);

        setValue('remittanceContext', context ?? null, {
          shouldDirty: false,
          shouldValidate: true,
        });

        if (nextRemittanceLines.length === 0) {
          setValue('remittanceLines', [], {
            shouldDirty: false,
            shouldValidate: true,
          });

          return;
        }

        setValue('remittanceLines', nextRemittanceLines, {
          shouldDirty: false,
          shouldValidate: true,
        });
      })
      .catch(() => {
        setRemittanceContext(null);
        setRemittanceTemplate(null);

        setValue('remittanceContext', null, {
          shouldDirty: false,
          shouldValidate: true,
        });

        setValue('remittanceLines', [], {
          shouldDirty: false,
          shouldValidate: true,
        });
      })
      .finally(() => {
        setIsContextLoading(false);
      });
  }, [deductionId, paymentMethodValue]);

  /**
   * Unified Create/Edit amount synchronization.
   *
   * IMPORTANT:
   *
   * - Existing row(s) loading does NOT trigger synchronization.
   * - Popup edits do NOT trigger synchronization.
   * - Only Settled Amount changes trigger synchronization.
   * - Exactly one remittance row -> sync amount.
   * - Multiple remittance rows -> leave them unchanged.
   */
  useEffect(() => {
    const currentAmountValue = String(amountValue ?? '');

    const previousAmountValue = previousDeductionAmountRef.current;

    // Always record the latest settled amount.
    previousDeductionAmountRef.current = currentAmountValue;

    // Initial initialization.
    if (previousAmountValue === null) {
      return;
    }

    // Settled Amount itself did not change.
    if (previousAmountValue === currentAmountValue) {
      return;
    }

    const isCheckPayment = (paymentMethodValue || '').toUpperCase() === 'CHECK';

    if (!isCheckPayment || !remittanceContext || !remittanceTemplate) {
      return;
    }

    // Read current remittance rows directly.
    // Do not depend on remittanceLinesValue.
    const currentLines = getValues('remittanceLines') || [];

    // Synchronize only when exactly
    // one row exists.
    if (!Array.isArray(currentLines) || currentLines.length !== 1) {
      return;
    }

    const rawAmount = currentAmountValue.trim();

    const parsedAmount = rawAmount ? Number(rawAmount.replace(/[$,\s]/g, '')) : null;

    const nextAmount = Number.isFinite(parsedAmount as number) ? parsedAmount : null;

    const firstLine = currentLines[0];

    if (firstLine.amount === nextAmount) {
      return;
    }

    setValue(
      'remittanceLines',
      [
        {
          ...firstLine,
          amount: nextAmount,
        },
      ],
      {
        shouldDirty: true,
        shouldValidate: true,
      },
    );
  }, [amountValue, paymentMethodValue, remittanceContext, remittanceTemplate, getValues, setValue]);

  useEffect(() => {
    if (!isAssignedToggleValue) {
      setValue('defendant', {
        id: '',
        label: '',
      });
    }
  }, [isAssignedToggleValue, setValue]);

  useEffect(() => {
    if (!isInitialLoadRef.current) {
      formMethods.trigger('amount');
    }
  }, [preSettlementAmountValue, formMethods]);

  return (
    <>
      <Box className={styles.classes.pageContainer}>
        <FormProvider {...formMethods}>
          <form className={styles.classes.formContainer}>
            <Box className={styles.classes.formInfoContainer}>
              <Box className={styles.classes.formInnerWrapper}>
                <Box className={styles.classes.formWrapper}>
                  <Box className={styles.classes.formFirstInfoBlock}>
                    <Typography className={styles.classes.fieldHeadline}>Identity</Typography>

                    <Box mt="16px">
                      <FormCopyTextField entityName="Deduction" name="id" label="Id" isReadOnly />
                    </Box>

                    <Box display="flex" justifyContent="space-between" alignItems="flex-end" mt="32px">
                      <Typography className={styles.classes.fieldHeadline}>Case</Typography>

                      {canViewCases && <FormLinkButton path={`/cases/${caseIdValue}/home`} />}
                    </Box>

                    <Box mt="16px">
                      <FormTextField name="referenceId" label="Reference Id" readOnly isReadOnly />
                    </Box>

                    <Box mt="24px">
                      <FormTextField name="caseSystemName" label="System Name" readOnly isReadOnly />
                    </Box>

                    <Box display="flex" justifyContent="space-between" alignItems="center" mt="32px">
                      <Typography className={styles.classes.fieldHeadline}>Defendant</Typography>

                      <Box display="flex" gap="16px" alignItems="center" paddingLeft="10px">
                        <FormToggleField name="isAssigned" label="Assigned" disabled={!canEdit} />

                        {canViewDefendants && (
                          <FormLinkButton
                            path={defendantIdValue ? `/defendants/${defendantIdValue}/home` : '#'}
                            disabled={!defendantIdValue}
                          />
                        )}
                      </Box>
                    </Box>

                    <Box mt="10px">
                      <FormAutocompleteFieldServerSearch
                        key={caseIdValue ? String(caseIdValue) : 'no-case'}
                        name="defendant"
                        label="Defendant"
                        optionsQuery={handleGetDefendants}
                        mapQueryResults={mapDefendantsQueryResults}
                        disabled={!canEdit || !isAssignedToggleValue}
                        filters={{
                          caseId: caseIdValue,
                        }}
                        isOptionEqualToValue={(option, value) => String(option.id) === String(value.id)}
                        fetchSize={100}
                        minSearchChars={3}
                      />
                    </Box>
                  </Box>

                  <Box className={styles.classes.formSecondInfoBlock}>
                    <Typography className={styles.classes.fieldHeadline}>Billed Amount</Typography>

                    <Box m="16px 0px 16px 0px">
                      <FormTextField name="preSettlementAmount" label="Billed Amount *" valueDefinition="$" disabled={!canEdit} />
                    </Box>

                    <Typography className={styles.classes.fieldHeadline}>Settled Amount</Typography>

                    <Box mt="16px">
                      <FormTextField name="amount" label="Settled Amount *" valueDefinition="$" disabled={!canEdit} />
                    </Box>

                    <Typography className={styles.classes.typeSectionHeadline}>Type</Typography>

                    <Box mt="16px">
                      <FormSelectField
                        name="type"
                        label="Type *"
                        options={DEDUCTION_TYPES}
                        width="100%"
                        disabled={!canEdit}
                        customOnChange={(value) => handleTypeChange(value)}
                      />
                    </Box>
                  </Box>
                </Box>

                <Box display="flex" width="100%" mt="8px">
                  <Box display="flex" flexDirection="column" width="100%">
                    {/* <Typography className={styles.classes.typeSectionHeadline}>Description</Typography>

                    <Box mt="16px">
                      <FormTextField name="description" label="Description" isMultiline isDescription disabled={!canEdit} />
                    </Box> */}

                    {isVendorType && (
                      <>
                        <Typography className={styles.classes.typeSectionHeadline}>Vendor Account</Typography>

                        <Box mt="16px">
                          <FormAutocompleteFieldServerSearch
                            key={`vendor-${typeFieldValue}`}
                            name="vendorAccount"
                            label="Vendor Account *"
                            optionsQuery={handleGetActiveVendors}
                            mapQueryResults={mapVendorsQueryResults}
                            disabled={!canEdit}
                            isOptionEqualToValue={(option, value) => String(option.id) === String(value.id)}
                            fetchSize={100}
                            minSearchChars={3}
                          />
                        </Box>
                      </>
                    )}

                    {isVendorType && vendorAccountIdValue && (
                      <>
                        <Typography className={styles.classes.typeSectionHeadline}>Intent</Typography>

                        <Box mt="16px">
                          <FormSelectField
                            name="disposition"
                            label="Intent *"
                            options={INTENT_TYPE_OPTIONS}
                            width="100%"
                            disabled={!canEdit}
                          />
                        </Box>

                        <Typography className={styles.classes.typeSectionHeadline}>Payment Method</Typography>

                        <Box mt="16px">
                          <FormSelectField
                            name="paymentMethod"
                            label={dispositionValue === 'External' ? 'Payment Method' : 'Payment Method *'}
                            options={DEDUCTION_PAYMENT_METHOD_OPTIONS}
                            width="100%"
                            disabled={!canEdit}
                            shouldClearable={dispositionValue === 'External'}
                          />

                          {dispositionValue !== 'External' && isPaymentAccountRequired && (
                            <Box mt="16px">
                              <FormSelectField
                                name='paymentAccount.id'
                                label='Account *'
                                options={paymentAccountOptions}
                                width='100%'
                                renderSelectedValue={renderPaymentAccountSelectedValue}
                                renderOption={renderPaymentAccountOption}
                                customOnChange={(value) => {
                                  const selected = paymentAccountOptions.find((item) => item.id === value);
                                  setValue(
                                    'paymentAccount',
                                    selected
                                      ? { id: selected.id, label: selected.label }
                                      : null,
                                    { shouldDirty: true, shouldValidate: true },
                                  );
                                }}
                              />

                              {renderPaymentAccountInfo(
                                selectedPaymentAccount,
                                vendorAccountIdValue,
                              )}
                            </Box>
                          )}

                          {(isPaymentMethodMissing || isPaymentProvisioningIssue) &&
                            dispositionValue !== 'External' &&
                            isPaymentAccountRequired && (
                              <Box display="flex" flexDirection="row" alignItems="center" gap="8px" mt={1}>
                                <Typography color="error" variant="body2" mt='8px'>
                                  {isPaymentMethodMissing
                                    ? 'Missing Account Details'
                                    : 'Some issues with the passport provisioning.'}
                                </Typography>

                                {canViewVendors && (
                                  <FormLinkButton path={`/vendors/edit-vendor-account/${vendorAccountIdValue}`} />
                                )}
                              </Box>
                            )}
                        </Box>
                      </>
                    )}

                    {typeFieldValue === 'Expense Reimbursement' && (
                      <>
                        <Typography className={styles.classes.typeSectionHeadline}>Intent</Typography>

                        <Box mt="16px">
                          <FormSelectField name="disposition" label="Intent *" options={INTENT_TYPE_OPTIONS} width="100%" />
                        </Box>
                      </>
                    )}

                    {dispositionValue === 'External' && typeFieldValue === 'Expense Reimbursement' && (
                      <>
                        <Typography className={styles.classes.typeSectionHeadline}>Payment Method</Typography>

                        <Box mt="16px">
                          <FormSelectField
                            name="paymentMethod"
                            label="Payment Method"
                            options={DEDUCTION_PAYMENT_METHOD_OPTIONS}
                            width="100%"
                            shouldClearable
                          />
                        </Box>
                      </>
                    )}

                    {dispositionValue === 'External' &&
                      ((isVendorType && vendorAccountIdValue) || typeFieldValue === 'Expense Reimbursement') && (
                        <>
                          <Box mt="16px">
                            <FormTextField name="externalPaymentReference" label="Payment Reference" />
                          </Box>

                          <Box mt="16px">
                            <FormDateField name="externalPaymentDate" label="Payment Date" maxDate={moment()} />
                          </Box>
                        </>
                      )}

                    <Typography className={styles.classes.typeSectionHeadline}>Upload Invoice</Typography>

                    <Box mt="16px">
                      <FormPDFUploadField
                        name="uploadInvoice"
                        label="Upload Invoice"
                        existingFileName={entityData?.docFileName as string | undefined}
                        onDownload={handleDownloadFile}
                        onDelete={onDeleteInvoiceButtonClick}
                        disabled={!canEdit}
                      />
                    </Box>

                    {isVendorType &&
                      vendorAccountIdValue &&
                      (paymentMethodValue || '').toUpperCase() === 'CHECK' &&
                      hasRequestedRemittanceContext && (
                        <>
                          <Typography className={styles.classes.typeSectionHeadline}>Check Memo</Typography>
                          <Typography fontSize='12px' color='text.secondary' mt='5px'>
                            This prints on the memo line of the check.
                          </Typography>

                          <Box mt='20px'>
                            <FormTextField name='memo' label='Check Memo' isMultiline isDescription disabled={!canEdit}  />
                          </Box>

                          <Box mt="20px">
                            <RemittanceAccordion
                              remittanceContext={remittanceContext}
                              remittanceLines={remittanceLinesValue}
                              deductionAmount={amountValue}
                              onChangeRemittanceLines={(lines) => {
                                setValue('remittanceLines', lines, {
                                  shouldDirty: true,
                                  shouldValidate: true,
                                });
                              }}
                              isLoading={isContextLoading}
                              canUpdate={canUpdate}
                              onFormOpenChange={setIsRemittanceFormOpen}
                            />
                          </Box>
                        </>

                      )}
                  </Box>
                </Box>

                {canEdit && (
                  <Box display="flex" width="100%" justifyContent="center">
                    <Box display="flex" mt="32px" maxWidth="416px" flexDirection="column" gap="16px" width="100%">
                      <FormSubmitButton
                        disabled={
                          !isValid ||
                          !isDirty ||
                          isRemittanceFormOpen ||
                          (isAssignedToggleValue && !defendantIdValue) ||
                          isVendorAccountMissing ||
                          isFormSubmitting ||
                          !isCheckRemittanceBalanced ||
                          (isVendorType &&
                            dispositionValue !== 'External' &&
                            (!vendorAccountIdValue ||
                              !paymentMethodValue ||
                              !!isPaymentMethodMissing ||
                              !!isPaymentProvisioningIssue ||
                              (isPaymentAccountRequired && !paymentAccountValue?.id)))
                        }
                        buttonIcon={<SaveIcon />}
                        buttonText="Save"
                        onClick={handleSubmit(onSubmitForm)}
                      />

                      <FormCancelButton disabled={!isDirty || isRemittanceFormOpen} onClick={handleCancelChanges} />
                    </Box>
                  </Box>
                )}
              </Box>

              {(canDelete || canCreateRecovery) && (
                <Box className={styles.classes.formActionsWrapper}>
                  <Typography className={styles.classes.fieldHeadline}>Actions</Typography>

                  {canDelete && (
                    <Box mt="16px">
                      <CommonButton
                        disabled={entityData?.status !== 'NotStarted'}
                        type="error"
                        buttonText="delete"
                        buttonIcon={<CancelIcon />}
                        onButtonClick={onDeleteButtonClick}
                      />
                    </Box>
                  )}

                  {canCreateRecovery && (
                    <Box mt="16px">
                      <CommonButton
                        disabled={entityData?.status !== 'Paid'}
                        type="infoMain"
                        buttonText="Create Recovery"
                        buttonIcon={<AddIcon />}
                        onButtonClick={() => setIsLienRecoveryPopupOpen(true)}
                      />
                    </Box>
                  )}
                </Box>
              )}
              
            </Box>
          </form>
        </FormProvider>

        <Popup
          isOpen={isDeletePopupOpen}
          headlineText={POPUP_DESCRIPTIONS.deductionDelete.headlineText}
          contentText={POPUP_DESCRIPTIONS.deductionDelete.contentText}
          onMainButtonClick={handleDeleteDeduction}
          onSecondaryButtonClick={handleCloseDeletePopup}
        />

        <Popup
          isOpen={isDeleteInvoicePopupOpen}
          headlineText={POPUP_DESCRIPTIONS.deductionInvoiceDelete.headlineText}
          contentText={POPUP_DESCRIPTIONS.deductionInvoiceDelete.contentText}
          onMainButtonClick={handleDeleteFile}
          onSecondaryButtonClick={handleCloseDeleteInvoicePopup}
        />

        <Popup
          isOpen={isFailedAccountConfirmOpen}
          headlineText={POPUP_DESCRIPTIONS.deductionFailedAccountConfirm.headlineText}
          contentText={POPUP_DESCRIPTIONS.deductionFailedAccountConfirm.contentText}
          mainButtonText="Yes"
          secondaryButtonText="No"
          onMainButtonClick={handleConfirmFailedAccountSubmit}
          onSecondaryButtonClick={handleCancelFailedAccountSubmit}
        />

        {deductionId && (
          <LienRecoveryPopup
            isOpen={isLienRecoveryPopupOpen}
            lien={{
              id: deductionId,
              providerName: (entityData?.vendorAccount as { label?: string } | null)?.label || '-',
              paidAmount: Number(entityData?.amount) || 0,
              paidAt: entityData?.paidAt as string | null,
            }}
            onSuccess={() => setIsLienRecoveryPopupOpen(false)}
            onCancel={() => setIsLienRecoveryPopupOpen(false)}
          />
        )}
      </Box>
    </>
  );
};

export default DeductionsEditFormContainer;